import React, { useState } from 'react';
import { Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const ContactPage = () => {
  const { t, currentLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const offices = [
    {
      country: 'Türkiye',
      flag: '🇹🇷',
      address: 'TEKNOPARK Gaziantep Üniversitesi Çamtepe Mah. Mahmut Tevfik Atay Bulvarı 4/A No:1',
      city: 'Gaziantep/Türkiye',
      email: 'info@co2neutralpage.com'
    },
    {
      country: 'Deutschland',
      flag: '🇩🇪',
      address: 'Hasenheide 115',
      city: 'D-10967 Berlin',
      email: 'info@co2neutralpage.com'
    },
    {
      country: 'España',
      flag: '🇪🇸',
      address: 'Pg. Joan de Borbó 99, Plantas 1 y 2 (Monday)',
      city: '08039 Barcelona (Spain)',
      email: 'info@co2neutralpage.com'
    }
  ];

  const currentOffice = offices.find(office => 
    (currentLanguage === 'tr' && office.country === 'Türkiye') ||
    (currentLanguage === 'de' && office.country === 'Deutschland') ||
    (currentLanguage === 'en' && office.country === 'España')
  ) || offices[0];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 1000);
  };

  return (
    <section className="min-h-screen bg-neutral-50 py-12 pt-32">
      <div className="container-custom max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-800 mb-6">
            {t('contact-page-title')}
          </h1>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            {t('contact-page-subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-neutral-800 mb-6 flex items-center gap-2">
              <Send className="h-6 w-6 text-primary-500" />
              {t('contact-form-title')}
            </h2>

            {isSubmitted ? (
              <div className="text-center py-8">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                  {t('contact-success-title')}
                </h3>
                <p className="text-neutral-600 mb-6">
                  {t('contact-success-message')}
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="btn-primary"
                >
                  {t('contact-new-message')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      {t('contact-form-name')}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={t('contact-form-name-placeholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      {t('contact-form-email')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={t('contact-form-email-placeholder')}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('contact-form-subject')}
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t('contact-form-subject-placeholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('contact-form-message')}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    placeholder={t('contact-form-message-placeholder')}
                  />
                </div>
                <button
                  type="submit"
                  className="w-full btn-primary py-4 text-lg"
                >
                  {t('contact-form-submit')}
                </button>
              </form>
            )}
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Main Office */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-semibold text-neutral-800 mb-6 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary-500" />
                {currentOffice.country}
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-primary-500 mt-1" />
                  <div>
                    <p className="text-neutral-800 font-medium">{currentOffice.address}</p>
                    <p className="text-neutral-600">{currentOffice.city}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-primary-500" />
                  <span className="text-neutral-800">{currentOffice.email}</span>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-semibold text-neutral-800 mb-6 flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary-500" />
                {t('contact-business-hours')}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-neutral-600">{t('contact-monday-friday')}</span>
                  <span className="text-neutral-800 font-medium">09:00 - 18:00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600">{t('contact-saturday')}</span>
                  <span className="text-neutral-800 font-medium">10:00 - 16:00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600">{t('contact-sunday')}</span>
                  <span className="text-neutral-800 font-medium">{t('contact-closed')}</span>
                </div>
              </div>
            </div>

            {/* Quick Contact */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-8 text-white">
              <h3 className="text-xl font-semibold mb-4">{t('contact-quick-contact')}</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5" />
                  <span>{currentOffice.email}</span>
                </div>
                <p className="text-primary-100 text-sm mt-4">
                  {t('contact-quick-contact-desc')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-16">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-neutral-800 mb-6 text-center">
              {t('contact-map-title')}
            </h3>
            <div className="bg-neutral-200 rounded-lg h-64 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <p className="text-neutral-600">{t('contact-map-placeholder')}</p>
                <p className="text-sm text-neutral-500 mt-2">{currentOffice.address}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactPage; 